import { useOutlookStore, useResourceStore, useSettingStore, useThemeStore } from "@shared/stores";
import React, { useCallback, useState } from "react";
import HStack from "@components/layout/HStack";
import TitleSetting from "@components/ui/TitleSetting";
import Divider from "@components/ui/Divider";
import Accordion from "@components/ui/Accordion";
import { settingLabels } from "@shared/constants/setting";
import { OrganizerSettingEntity } from "@features/settings/services/entities/organizerSettingEntity";
import { useCreateOrganizerSetting } from "@features/settings/services/useCreateOrganizerSetting";
import { useGlobalContext } from "@shared/contexts/GlobalContext";
import { useTranslation } from "react-i18next";
import SelectCategoryForm from "@features/search/components/SelectCategoryForm";
import { CategoryIcon } from "@/pages/taskpane/components/icons/IconCustom";
import { filterResourceDefault } from "@/pages/taskpane/shared/constants/appConstants";

const DefaultCategoriesSetting = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useThemeStore();
  const [isOpen, setIsOpen] = useState(false);

  const header = (
    <HStack justifyContent="between" className="w-full" onClick={() => setIsOpen(!isOpen)}>
      <TitleSetting
        icon={<CategoryIcon isDarkMode={isDarkMode} size={20} />}
        label={t(settingLabels.defaultCategories || "Default Categories")}
      />
    </HStack>
  );

  return (
    <Accordion header={header} isOpen={isOpen} onToggleOpen={() => setIsOpen(!isOpen)}>
      {isOpen ? <DefaultCategoriesContent /> : null}
    </Accordion>
  );
};

export default DefaultCategoriesSetting;

// CONTENT

const DefaultCategoriesContent = () => {
  const { t } = useTranslation();
  const { setIsLoadingOverlay } = useGlobalContext();
  // const showToast = useToastStore((state) => state.showToast);
  const { organizer } = useOutlookStore();
  const { defaultCategoryIds, updateDefaultCategories } = useSettingStore();
  const { filterResource, setFilterResource } = useResourceStore();

  const { mutate: updateDefaultCategoriesMutation } = useCreateOrganizerSetting({
    mutationConfig: {
      onSuccess: (data: OrganizerSettingEntity) => {
        updateDefaultCategories(data.defaultCategoryIds);
        // showToast(t("Categories changed"), true);
        setFilterResource({
          ...(filterResource || filterResourceDefault),
          categories: data?.defaultCategoryIds ? data.defaultCategoryIds.split(",").map((i) => parseInt(i)) : [],
        });
      },
      onError: () => {
        // showToast(t("Categories change failed"), false);
      },
      onSettled: () => {
        setIsLoadingOverlay(false);
      },
    },
  });

  const handleApply = useCallback(
    (selectedIds: number[]) => {
      if (!organizer) return;
      setIsLoadingOverlay(true);

      updateDefaultCategoriesMutation({
        organizerEmail: organizer,
        defaultCategoryIds: selectedIds.join(","),
      });
    },
    [organizer, updateDefaultCategoriesMutation, setIsLoadingOverlay]
  );

  const handleClear = useCallback(() => {
    if (!organizer) return;
    setIsLoadingOverlay(true);

    updateDefaultCategoriesMutation({
      organizerEmail: organizer,
      defaultCategoryIds: "",
    });
  }, [organizer, updateDefaultCategoriesMutation, setIsLoadingOverlay]);

  const categorySelected = defaultCategoryIds
    ? defaultCategoryIds
        .split(",")
        .map((i) => parseInt(i))
        .filter((i) => !isNaN(i))
    : [];

  return (
    <div className="w-full">
      <Divider className="mt-3" />
      <SelectCategoryForm
        categorySelected={categorySelected}
        onClear={handleClear}
        onApply={handleApply}
        iShowFooter={true}
      />
    </div>
  );
};
